import { memo, useCallback, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { dashboardApi } from "@/services/dashboardApi";
import {
  ArrowUpRight,
  BarChart3,
  BookOpen,
  Bot,
  CalendarClock,
  Gauge,
  LifeBuoy,
  Loader2,
  MessageCircle,
  UserRound,
} from "lucide-react";

const NavigationItems = memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const pathname = location.pathname;
  const { isMobile, setOpenMobile } = useSidebar();

  // Create a single handler for all navigation clicks
  const handleNavClick = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const navigationSections = useMemo(
    () => [
      {
        label: t("sidebar.SectionOverview", "Overview"),
        items: [
          {
            title: t("sidebar.NavDashboard", "Analytics"),
            description: t(
              "sidebar.NavDashboardHint",
              "Track KPIs and spot trends at a glance"
            ),
            href: "/",
            match: "exact",
            icon: Gauge,
          },
          {
            title: t("sidebar.NavUsage", "Usage"),
            description: t(
              "sidebar.NavUsageHint",
              "Monitor consumption across teams"
            ),
            href: "/usage",
            activeMatch: "/usage",
            match: "startsWith",
            icon: BarChart3,
          },
        ],
      },
      {
        label: t("sidebar.SectionPlanning", "Planning"),
        items: [
          {
            title: t("sidebar.NavCalendar", "Calendar"),
            description: t(
              "sidebar.NavCalendarHint",
              "Plan handoffs and campaign moments"
            ),
            href: "/calendar",
            activeMatch: "/calendar",
            match: "startsWith",
            icon: CalendarClock,
            badge: t("sidebar.Beta", "Beta"),
            // badge: t("sidebar.BadgeNew", "New"),
          },
        ],
      },
      {
        label: t("sidebar.SectionOperations", "Operations"),
        items: [
          {
            title: t("sidebar.NavAgents", "Agents"),
            description: t(
              "sidebar.NavAgentsHint",
              "Manage automations and human fallbacks"
            ),
            href: "/agents",
            activeMatch: "/agents",
            match: "startsWith",
            icon: Bot,
          },
          {
            title: t("sidebar.NavIntegration", "Integrations"),
            description: t(
              "sidebar.NavIntegrationHint",
              "Connect channels and manage webhooks"
            ),
            href: "/messaging/instagram",
            activeMatch: "/messaging",
            match: "startsWith",
            icon: MessageCircle,
          },
        ],
      },
      {
        label: t("sidebar.SectionWorkspace", "Workspace"),
        items: [
          {
            title: t("sidebar.NavAccount", "Account"),
            description: t(
              "sidebar.NavAccountHint",
              "Profile, billing, and preferences"
            ),
            href: "/account",
            activeMatch: "/account",
            match: "startsWith",
            icon: UserRound,
          },
          {
            title: t("sidebar.NavDocs", "Documentation"),
            description: t(
              "sidebar.NavDocsHint",
              "Playbooks, changelog, and API docs"
            ),
            href: "/documentation",
            activeMatch: "/documentation",
            match: "startsWith",
            icon: BookOpen,
            trailingIcon: ArrowUpRight,
          },
        ],
      },
    ],
    [t]
  );

  return (
    <div className="space-y-6">
      {navigationSections.map((section) => (
        <div key={section.label} className="space-y-2">
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
            {section.label}
          </SidebarGroupLabel>
          <SidebarGroupContent className="space-y-1">
            <SidebarMenu>
              {section.items.map((item) => {
                const Icon = item.icon;
                const TrailingIcon = item.trailingIcon;
                const matchStrategy = item.match ?? "startsWith";
                const matchTarget = item.activeMatch ?? item.href;
                const isActive =
                  matchStrategy === "exact"
                    ? pathname === matchTarget
                    : pathname?.startsWith(matchTarget);

                return (
                  <SidebarMenuItem
                    key={item.title}
                    className={cn(
                      "before:absolute before:left-0 before:top-[6px] before:bottom-[6px] before:w-1 before:rounded-r-full before:bg-primary/40 before:opacity-0 before:transition-opacity group-hover/menu-item:before:opacity-40",
                      isActive && "before:opacity-100"
                    )}
                  >
                    <SidebarMenuButton
                      asChild
                      isActive={Boolean(isActive)}
                      className="items-start"
                    >
                      <Link to={item.href} onClick={handleNavClick}>
                        <Icon className="mr-3 mt-[2px] h-4 w-4 text-muted-foreground" />
                        <div className="flex flex-1 flex-col">
                          <span className="flex items-center gap-2 text-sm font-medium">
                            {item.title}
                            {item.badge ? (
                              <Badge className="rounded-full border-transparent bg-primary/15 px-2 py-0 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                {item.badge}
                              </Badge>
                            ) : null}
                          </span>
                          {item.description ? (
                            <span className="mt-1 text-xs text-muted-foreground/80">
                              {item.description}
                            </span>
                          ) : null}
                        </div>
                        {TrailingIcon ? (
                          <TrailingIcon className="ml-3 mt-[2px] h-4 w-4 text-muted-foreground/60" />
                        ) : null}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </div>
      ))}
    </div>
  );
});

NavigationItems.displayName = "NavigationItems";

export const DashboardSidebar = memo(() => {
  const { t } = useTranslation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { user } = useAuth0();

  const userEmail = user?.email ?? "";

  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);
  const [supportStatus, setSupportStatus] = useState(null);

  const handleCloseMobile = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const handleSupportToggle = useCallback((open) => {
    setIsSupportOpen(open);

    if (open) {
      setSupportStatus(null);
      return;
    }

    setSupportMessage("");
    setSupportStatus(null);
    setIsSubmittingSupport(false);
  }, []);

  const handleSubmitSupport = useCallback(
    async (event) => {
      event.preventDefault();

      if (!userEmail) {
        setSupportStatus({
          type: "error",
          message: t(
            "sidebar.SupportModalMissingEmail",
            "We couldn't detect your email. Please refresh and try again."
          ),
        });
        return;
      }

      if (!supportMessage.trim()) {
        setSupportStatus({
          type: "error",
          message: t(
            "sidebar.SupportModalEmptyMessage",
            "Please share a few details so we can help."
          ),
        });
        return;
      }

      setIsSubmittingSupport(true);
      setSupportStatus(null);

      try {
        await dashboardApi.support.submitRequest({
          email: userEmail,
          message: supportMessage.trim(),
        });

        setSupportStatus({
          type: "success",
          message: t(
            "sidebar.SupportModalSuccess",
            "Thanks! Our team will reach out shortly."
          ),
        });
        setSupportMessage("");
      } catch (error) {
        console.error("Failed to submit support request:", error);
        setSupportStatus({
          type: "error",
          message:
            error instanceof Error && error.message
              ? error.message
              : t(
                  "sidebar.SupportModalError",
                  "We couldn't send your message. Please try again."
                ),
        });
      } finally {
        setIsSubmittingSupport(false);
      }
    },
    [supportMessage, t, userEmail]
  );

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex h-14 items-center justify-center border-b px-4">
          <Link to="/" onClick={handleCloseMobile}>
            <img
              src="/logo-no-background.svg"
              alt={t("sidebar.LogoAlt", "Company Logo")}
              style={{ width: '120px', height: '40px' }}
              className="object-contain"
            />
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent className="flex h-[calc(100vh-3.5rem)] flex-col justify-between gap-6 pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <SidebarGroup className="px-2">
          <NavigationItems />
        </SidebarGroup>

        <Dialog open={isSupportOpen} onOpenChange={handleSupportToggle}>
          <SidebarFooter className="px-3 pb-2">
            <div className="rounded-2xl border border-border/60 bg-muted/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <LifeBuoy className="h-4 w-4 text-primary" />
                  {t("sidebar.FooterTitle", "Need a hand?")}
                </div>
                {/* <Badge variant="secondary" className="rounded-full bg-emerald-100 px-2 py-0 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                  {t("sidebar.FooterStatus", "Online")}
                </Badge> */}
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                {t("sidebar.FooterDescription", "Ask about the portal.")}
              </p>
              <div className="mt-4 grid gap-2 text-xs">
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-between rounded-lg px-2 py-1.5 text-primary transition hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                  >
                    <span>{t("sidebar.FooterContact", "Contact support")}</span>
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </DialogTrigger>
              </div>
            </div>
          </SidebarFooter>

          <DialogContent className="sm:max-w-md">
            <DialogHeader className="text-left">
              <DialogTitle>
                {t("sidebar.SupportModalTitle", "Contact support")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "sidebar.SupportModalDescription",
                  "Tell us what's going on and we'll follow up by email."
                )}
              </DialogDescription>
            </DialogHeader>

            {supportStatus ? (
              <div
                className={cn(
                  "rounded-lg border px-3 py-2 text-sm",
                  supportStatus.type === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-destructive/20 bg-destructive/10 text-destructive"
                )}
              >
                {supportStatus.message}
              </div>
            ) : null}

            <form className="space-y-4" onSubmit={handleSubmitSupport}>
              <div className="space-y-1">
                <Label htmlFor="support-email">
                  {t("sidebar.SupportModalEmailLabel", "Your email")}
                </Label>
                <Input
                  id="support-email"
                  type="email"
                  value={userEmail || ""}
                  readOnly
                  disabled={!userEmail}
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="support-message">
                  {t("sidebar.SupportModalMessageLabel", "How can we help?")}
                </Label>
                <Textarea
                  id="support-message"
                  value={supportMessage}
                  onChange={(event) => {
                    setSupportMessage(event.target.value);
                    if (supportStatus?.type === "error") {
                      setSupportStatus(null);
                    }
                  }}
                  placeholder={t(
                    "sidebar.SupportModalMessagePlaceholder",
                    "Share any context, links, or questions."
                  )}
                  rows={5}
                />
              </div>

              <DialogFooter className="gap-2 sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleSupportToggle(false)}
                  className="sm:mr-auto button-main-secondary"
                >
                  {t("sidebar.SupportModalCancel", "Cancel")}
                </Button>
                <Button
                  type="submit"
                  className="button-main-primary"
                  disabled={
                    isSubmittingSupport || !supportMessage.trim() || !userEmail
                  }
                >
                  {isSubmittingSupport ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("sidebar.SupportModalSubmitting", "Sending...")}
                    </>
                  ) : (
                    t("sidebar.SupportModalSubmit", "Send message")
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </SidebarContent>
    </Sidebar>
  );
});

DashboardSidebar.displayName = "DashboardSidebar";
